import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Calendar, User, Wallet, Trash2, Edit2, CreditCard, Building, Receipt, Printer, FileText } from 'lucide-react';
import { Card, Button, Input, Badge, Select } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const CashPaymentForm = ({ onSuccess, initialData, especeModeId }) => {
    const { success, error: toastError, warning } = useToast();
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        type: 'expense',
        transaction_date: initialData?.transaction_date || new Date().toISOString().split('T')[0],
        owner_name: initialData?.owner_name || user?.name || '',
        account_number: initialData?.account_number || 'الصندوق',
        bank_name: initialData?.bank_name || 'الصندوق',
        transaction_number: initialData?.transaction_number || '',
        notes: initialData?.notes || '',
        payment_mode_id: initialData?.payment_mode_id || especeModeId,
        expense_ids: initialData?.expenses?.map(r => r.id) || []
    });

    const [selectedSupplierId, setSelectedSupplierId] = useState(() => {
        if (initialData?.expenses?.length > 0) {
            return initialData.expenses[0].supplier_id?.toString() || '';
        }
        return '';
    });

    const { data: expensesRaw, loading: expensesLoading } = useGet('/expenses/unpaid');
    const { data: suppliersResponse } = useGet('/suppliers');

    const suppliers = Array.isArray(suppliersResponse) ? suppliersResponse : (suppliersResponse?.data || []);
    
    const unpaidExpenses = useMemo(() => {
        const pending = expensesRaw?.data || expensesRaw || [];
        const linked = initialData?.expenses || [];
        const merged = [...linked];
        pending.forEach(p => { if (!merged.some(l => l.id === p.id)) merged.push(p); });
        
        if (!selectedSupplierId) return [];
        return merged.filter(exp => exp.supplier_id?.toString() === selectedSupplierId.toString());
    }, [expensesRaw, initialData, selectedSupplierId]);

    // Auto-check expenses when supplier changes (only for new payments)
    useEffect(() => {
        if (!initialData && selectedSupplierId && unpaidExpenses.length > 0 && formData.expense_ids.length === 0) {
            setFormData(prev => ({
                ...prev,
                expense_ids: unpaidExpenses.map(exp => exp.id)
            }));
        }
    }, [selectedSupplierId, unpaidExpenses, initialData]);

    const handleCheckboxChange = (id) => {
        setFormData(prev => {
            const current = prev.expense_ids;
            return { ...prev, expense_ids: current.includes(id) ? current.filter(rid => rid !== id) : [...current, id] };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.payment_mode_id) return warning('وسيلة الدفع غير متوفرة');
        if (formData.expense_ids.length === 0) return warning('يرجى اختيار مصروف واحد على الأقل');

        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/bank-transactions/${initialData.id}`, formData);
                success('تم التحديث بنجاح');
            } else {
                await api.post('/bank-transactions', formData);
                success('تم التسجيل بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    const totalAmount = unpaidExpenses
        .filter(exp => formData.expense_ids.includes(exp.id))
        .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    return (
        <form onSubmit={handleSubmit} className="space-y-6 text-right">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="تاريخ العملية" type="date" required value={formData.transaction_date} onChange={e => setFormData({...formData, transaction_date: e.target.value})} icon={Calendar} />
                <Select label="المورد" required options={suppliers.map(sup => ({ value: sup.id, label: sup.name }))} value={selectedSupplierId} onChange={e => { setSelectedSupplierId(e.target.value); setFormData(prev => ({ ...prev, expense_ids: [] })); }} icon={Building} disabled={!!initialData} />
                <Input label="وسيلة الدفع" value="ESPECE" disabled icon={CreditCard} />
                
                <div className="md:col-span-2">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-700">المبلغ الإجمالي للمصاريف المحددة</span>
                            <span className="text-xl font-bold text-primary">{totalAmount.toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} درهم</span>
                        </div>
                    </div>
                </div>
            </div>

            {selectedSupplierId ? (
                <div className="space-y-3">
                    <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Receipt size={18} className="text-primary" />
                        المصاريف المرتبطة بالمورد
                    </h4>
                    {expensesLoading ? (
                        <div className="py-8 text-center text-slate-400">جاري تحميل المصاريف...</div>
                    ) : unpaidExpenses.length > 0 ? (
                        <div className="border border-slate-200 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                                    <tr>
                                        <th className="px-4 py-3 text-right w-12">تحديد</th>
                                        <th className="px-4 py-3 text-right">التاريخ</th>
                                        <th className="px-4 py-3 text-right">النوع</th>
                                        <th className="px-4 py-3 text-left">المبلغ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unpaidExpenses.map(exp => (
                                        <tr key={exp.id} className={`border-b border-slate-100 last:border-0 ${formData.expense_ids.includes(exp.id) ? 'bg-primary/5' : ''}`}>
                                            <td className="px-4 py-3">
                                                <input 
                                                    type="checkbox" 
                                                    checked={formData.expense_ids.includes(exp.id)}
                                                    onChange={() => handleCheckboxChange(exp.id)}
                                                    className="w-4 h-4 rounded text-primary focus:ring-primary"
                                                />
                                            </td>
                                            <td className="px-4 py-3">{exp.transaction_date}</td>
                                            <td className="px-4 py-3">{exp.expense_type?.name}</td>
                                            <td className="px-4 py-3 text-left font-bold text-slate-700">
                                                {parseFloat(exp.amount).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} درهم
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-8 text-center bg-slate-50 rounded-xl border border-slate-100 text-slate-500 text-sm">
                            لا توجد مصاريف غير مسددة لهذا المورد
                        </div>
                    )}
                </div>
            ) : (
                <div className="py-8 text-center bg-slate-50 rounded-xl border border-slate-100 text-amber-500 font-medium text-sm">
                    يرجى اختيار المورد أولاً لظهور المصاريف
                </div>
            )}

            <div className="flex justify-end pt-4 border-t">
                <Button type="submit" isLoading={submitting} disabled={formData.expense_ids.length === 0} className="px-10">
                    حفظ الدفع النقدي
                </Button>
            </div>
        </form>
    );
};

const CashPaymentsList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const { success, error: toastError } = useToast();
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('bank', 'create');
    const canDelete = hasPermission('bank', 'delete');
    
    // We need to fetch the payment modes to find the ESPECE ID
    const { data: paymentModesRaw } = useGet('/payment-modes');
    const paymentModes = Array.isArray(paymentModesRaw) ? paymentModesRaw : (paymentModesRaw?.data || []);
    const especeMode = paymentModes.find(pm => pm.name?.toUpperCase() === 'ESPECE');
    const especeModeId = especeMode ? especeMode.id : '';

    const { data: transactionsResponse, loading, refresh } = useGet(
        especeModeId ? `/bank-transactions?type=expense&payment_mode_id=${especeModeId}&page=${currentPage}&search=${searchTerm}` : null
    );

    const transactions = transactionsResponse?.data || [];
    const pagination = transactionsResponse?.meta || null;

    const { data: treasuryResponse } = useGet('/bank-transactions?type=treasury_feed&all=true');

    const treasuryFeeds = Array.isArray(treasuryResponse) ? treasuryResponse : (treasuryResponse?.data || []);

    const totalCashAmount = transactions.reduce((sum, tx) => sum + (parseFloat(tx.total_amount) || 0), 0);
    const totalTreasuryAmount = treasuryFeeds.reduce((sum, tx) => sum + (parseFloat(tx.total_amount) || 0), 0);
    const remainingTreasuryAmount = totalTreasuryAmount - totalCashAmount;

    useEffect(() => {
        const querySearch = searchParams.get('search');
        if (querySearch !== null) {
            setSearchTerm(querySearch);
            setCurrentPage(1);
        }
    }, [searchParams]);

    const handleDelete = async (id) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه العملية النقدية؟')) return;
        try {
            await api.delete(`/bank-transactions/${id}`);
            success('تم حذف العملية النقدية بنجاح');
            refresh();
        } catch (error) {
            toastError('خطأ أثناء الحذف');
        }
    };

    return (
        <div className="space-y-6 text-right" dir="rtl">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800 flex items-center gap-2">
                        <Wallet className="w-6 h-6 text-primary" />
                        المدفوعات النقدية
                    </h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">عرض المصاريف التي تم تسديدها نقداً (ESPECE)</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {canCreate && (
                        <Button onClick={() => { setEditingTransaction(null); setIsModalOpen(true); }} disabled={!especeModeId} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" /> تسجيل مدفوعة نقدية
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow bg-emerald-50/50">
                    <span className="text-xs font-bold text-emerald-600 uppercase">مجموع المبلغ في الخزينة</span>
                    <span className="text-xl font-bold text-emerald-700">{totalTreasuryAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow bg-red-50/50">
                    <span className="text-xs font-bold text-red-600 uppercase">إجمالي المصاريف</span>
                    <span className="text-xl font-bold text-red-700">{totalCashAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow bg-blue-50/50">
                    <span className="text-xs font-bold text-blue-600 uppercase">باقي الخزينة</span>
                    <span className="text-xl font-bold text-blue-700">{remainingTreasuryAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow bg-slate-50/50">
                    <span className="text-xs font-bold text-slate-600 uppercase">عدد المصاريف</span>
                    <span className="text-xl font-bold text-slate-800">{transactions.length}</span>
                </Card>
            </div>

            <Card className="p-0 overflow-hidden bg-white border-slate-200">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <Input 
                            placeholder="بحث برقم العملية أو المورد أو المبلغ..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pr-10 bg-white"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-right">التاريخ</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-right">رقم العملية</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-right">اسم المورد</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-right">صاحب العملية</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-right">المبلغ الإجمالي</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase font-bold text-left">الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">جاري التحميل...</td></tr>
                            ) : transactions.length === 0 ? (
                                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500 font-medium">لا توجد مدفوعات نقدية مطابقة للبحث</td></tr>
                            ) : (
                                transactions.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-slate-50/80 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-slate-600 font-medium">
                                                <Calendar size={14} className="text-slate-400" />
                                                {tx.transaction_date}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-slate-700">
                                            {tx.transaction_number || `#TX-${tx.id}`}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                                                    {tx.expenses?.[0]?.supplier?.name?.charAt(0) || '-'}
                                                </div>
                                                <span className="font-bold text-slate-700">{tx.expenses?.[0]?.supplier?.name || '---'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">
                                            {tx.owner_name}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant="success" className="bg-green-50 text-green-700 border-green-200">
                                                {parseFloat(tx.total_amount).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} درهم
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-end gap-2 transition-opacity">
                                                {canCreate && (
                                                    <button onClick={() => { setEditingTransaction(tx); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors" title="تعديل">
                                                        <Edit2 size={16} />
                                                    </button>
                                                )}
                                                <button onClick={() => window.open(`/print/cash-payment/${tx.id}`, '_blank')} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="طباعة">
                                                    <Printer size={16} />
                                                </button>
                                                {canDelete && (
                                                    <button onClick={() => handleDelete(tx.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="حذف">
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination && pagination.last_page > 1 && (
                    <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <span className="text-xs text-slate-500 font-medium">
                            عرض {transactions.length} من {pagination.total} مدفوعة نقدية
                        </span>
                        <div className="flex gap-1">
                            {Array.from({ length: pagination.last_page }).map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                                        currentPage === i + 1 
                                        ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                    }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            <Modal 
                isOpen={isModalOpen} 
                onClose={() => { setIsModalOpen(false); setEditingTransaction(null); }} 
                title={editingTransaction ? "تعديل مدفوعة نقدية" : "تسجيل مدفوعة نقدية"} 
                size="3xl"
            >
                {isModalOpen && <CashPaymentForm 
                    onSuccess={() => { setIsModalOpen(false); refresh(); }} 
                    initialData={editingTransaction} 
                    especeModeId={especeModeId}
                />}
            </Modal>
        </div>
    );
};

export default CashPaymentsList;
